import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useClerk } from "@clerk/react";
import { useTypingPlaceholder } from "@/hooks/use-typing-placeholder";
import { HeroCanvas } from "./HeroCanvas";
import bgLight from "@/assets/landing/bg-light.svg";
import bgDark from "@/assets/landing/bg-dark.svg";

const HERO_PROMPTS = [
  "Generate product photos from text",
  "Turn a blog post into a short video",
  "Create a podcast from an article",
  "Remove backgrounds from 50 images",
  "Write and narrate a children's story",
  "Build a social media content pipeline",
  "Summarize papers into audio briefings",
  "Create a music video from lyrics",
  "Generate thumbnails for YouTube",
  "Turn screenshots into product mockups",
];

export function HeroV2() {
  const [prompt, setPrompt] = useState("");
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  const clerk = useClerk();
  const animatedPlaceholder = useTypingPlaceholder(HERO_PROMPTS);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) return;

    sessionStorage.setItem("rayloop_pending_prompt", prompt.trim());

    if (!isSignedIn) {
      clerk.openSignUp({
        
        fallbackRedirectUrl: "/dashboard",
      });
      return;
    }

    navigate("/dashboard");
  };

  return (
    <section className="relative w-full overflow-hidden">
      {/* Background images — full-width gradient SVGs */}
      <img
        src={bgLight}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 w-full select-none object-cover dark:hidden"
      />
      <img
        src={bgDark}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 hidden w-full select-none object-cover dark:block"
      />

      {/* Bottom gradient fade */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-80 sm:h-96 bg-gradient-to-b from-transparent via-background/60 to-background" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center px-6 pt-28 pb-16 sm:pb-0 sm:pt-36">
        {/* Heading */}
        <h1 className="text-white mx-auto mt-6 max-w-4xl text-center text-4xl font-display leading-[1.1] tracking-normal sm:text-6xl lg:text-7xl">
          Chain AI Models into
          <br />
          Automated Pipelines
        </h1>

        {/* Subtitle */}
        <p
          className="text-white mx-auto mt-4 max-w-[500px] text-center text-sm leading-relaxed sm:mt-6 sm:text-base lg:text-lg opacity-90 drop-shadow-md"
          style={{ textShadow: "0 2px 8px rgba(0,0,0,0.8)" }}
        >
          Build visual workflows that connect text, image, video, and audio AI
          models. From idea to finished content in a single click.
        </p>

        {/* Project-creation input — pill-shaped */}
        <form
          onSubmit={handleSubmit}
          className="mx-auto mt-10 w-full max-w-[440px]"
        >
          <div className="flex items-center rounded-full border border-white/30 bg-white/20 p-1.5 pr-2 backdrop-blur-xl shadow-lg shadow-black/10 dark:border-white/20 dark:bg-white/10">
            <input
              type="text"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={prompt ? "" : animatedPlaceholder}
              className="text-white placeholder:text-white/70 min-w-0 flex-1 bg-transparent px-3 py-1 text-[15px] outline-none"
            />
            <button
              type="submit"
              disabled={!prompt.trim()}
              className="shrink-0 rounded-full bg-brand px-6 py-1.5 text-[14px] font-medium text-brand-foreground transition-colors hover:bg-brand/90 disabled:opacity-80 cursor-pointer"
            >
              Create
            </button>
          </div>
        </form>

        {/* Hero illustration — animated pipeline canvas (hidden on mobile) */}
        <div className="relative z-10 mx-auto mt-20 hidden w-full max-w-[1100px] px-4 sm:block sm:px-6">
          <HeroCanvas />
        </div>
      </div>
    </section>
  );
}
